import React, { useMemo, useState } from 'react';
import { Edit2, PowerOff, Eye, RotateCcw, LinkIcon, Copy, RefreshCw, Users, AlertTriangle } from 'lucide-react';
import { Badge } from '../../../../components/ui/Badge';
import { Button } from '../../../../components/ui/Button';
import { Input } from '../../../../components/ui/Input';
import { Select } from '../../../../components/ui/Select';
import { ConfirmDialog } from '../../../../components/shared/ConfirmDialog';
import { Avatar } from '../../shared/Avatar';
import { formatRelativeTime } from '../../../../utils/formatters';
import { isLearnerAccount, getTeacherClassrooms, getLearnerExistingClassrooms, type Account, type AccountsState } from '../../../../accounts';
import { generateUniqueLinkCode } from '../../../../accounts';
import { getMasteryBand } from '../../../shared/masteryBands';
import { ROLE_CONFIG } from './types';
import type { UserRow } from './types';
interface UserDetailPanelProps {
  user: UserRow;
  isEditing: boolean;
  selectedMastery: Array<{
    subjectId: string;
    label: string;
    avg: number;
    count: number;
  }>;
  accounts: AccountsState | undefined;
  store: {
    updateAccountNameById: (id: string, name: string) => void;
    updateAccountEmail: (id: string, email: string) => void;
    updateTeacherDetails: (id: string, details: {
      schoolName: string;
    }) => void;
    updateLearnerDetails: (id: string, details: {
      grade: number;
    }) => void;
    deleteAccount: (id: string) => void;
    resetLearnerProgress: (id: string) => void;
    setLearnerLinkCode: (id: string, code: string) => void;
  };
  onNavigateToLearner?: (learnerId: string) => void;
  onClose: () => void;
  onStartEdit: (user: UserRow) => void;
  onStopEdit: () => void;
  onUserUpdate: (updater: (prev: UserRow | null) => UserRow | null) => void;
}
export function UserDetailPanel({
  user,
  isEditing,
  selectedMastery,
  accounts,
  store,
  onNavigateToLearner,
  onClose,
  onStartEdit,
  onStopEdit,
  onUserUpdate
}: UserDetailPanelProps) {
  const [resetConfirm, setResetConfirm] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [codeCopied, setCodeCopied] = useState(false);
  // Edit form state
  const [editUserName, setEditUserName] = useState(user.name);
  const [editUserEmail, setEditUserEmail] = useState(user.email !== '—' ? user.email : '');
  // Role is intentionally not editable from this panel — kept as a plain
  // derived value rather than useState to avoid the previous "misleading
  // const useState" pattern (audit V10 / P2-2).
  const editUserRole = user.role;
  const [editUserOrganization, setEditUserOrganization] = useState(user.organization !== '—' ? user.organization : '');
  const [editUserGrade, setEditUserGrade] = useState(user.account && isLearnerAccount(user.account) ? String(user.account.grade || 6) : '6');
  // Grade-mismatch warning when editing learner grade
  const gradeWarnings = useMemo(() => {
    if (editUserRole !== 'learner' || !accounts) return [];
    const newGrade = parseInt(editUserGrade, 10);
    const existing = getLearnerExistingClassrooms(accounts, user.id);
    const warnings: string[] = [];
    for (const {
      classroom,
      teacherName
    } of existing) {
      if (classroom.groupLevel && classroom.groupLevel !== newGrade) {
        warnings.push(`${user.name} zit in "${classroom.name}" (groep ${classroom.groupLevel}) van ${teacherName}. De nieuwe groep is ${newGrade}.`);
      }
    }
    return warnings;
  }, [editUserRole, editUserGrade, accounts, user.id, user.name]);
  const handleEditUser = (e: React.FormEvent) => {
    e.preventDefault();
    if (!editUserName.trim()) return;
    if (editUserName !== user.name) {
      store.updateAccountNameById(user.id, editUserName.trim());
    }
    if (editUserEmail !== (user.email !== '—' ? user.email : '')) {
      store.updateAccountEmail(user.id, editUserEmail.trim());
    }
    if (editUserRole === 'teacher' && editUserOrganization !== (user.organization !== '—' ? user.organization : '')) {
      store.updateTeacherDetails(user.id, {
        schoolName: editUserOrganization.trim()
      });
    }
    if (editUserRole === 'learner' && user.account && isLearnerAccount(user.account)) {
      const newGrade = parseInt(editUserGrade, 10);
      const currentGrade = user.account.grade || 6;
      if (newGrade !== currentGrade) {
        store.updateLearnerDetails(user.id, {
          grade: newGrade
        });
      }
    }
    onUserUpdate(prev => prev ? {
      ...prev,
      name: editUserName.trim(),
      email: editUserEmail.trim() || '—',
      organization: editUserOrganization.trim() || '—'
    } : null);
    onStopEdit();
  };
  if (isEditing) {
    return <div data-id="element-7557" data-mp="1a9444pk5wedd">
        <form onSubmit={handleEditUser} className="space-y-4" data-id="element-7558" data-mp="1a9ch0hgabjok">
          <Input label="Naam" type="text" required value={editUserName} onChange={e => setEditUserName(e.target.value)} data-id="element-7559" data-mp="3lwz6oewuzifm" />
          <Input label="E-mailadres" type="email" value={editUserEmail} onChange={e => setEditUserEmail(e.target.value)} data-id="element-7560" data-mp="3cx2o8v12bft0" />
          {editUserRole === 'teacher' && <Input label="School" type="text" value={editUserOrganization} onChange={e => setEditUserOrganization(e.target.value)} data-id="element-7561" data-mp="8jnql0aeh438" />}
          {editUserRole === 'learner' && <>
              <Select label="Groep" value={editUserGrade} onChange={e => setEditUserGrade(e.target.value)} options={[{
            value: '6',
            label: 'Groep 6'
          }, {
            value: '7',
            label: 'Groep 7'
          }, {
            value: '8',
            label: 'Groep 8'
          }]} data-id="element-7562" data-mp="4ghlhvz0xiyc" />
              {gradeWarnings.length > 0 && <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 space-y-1" data-id="element-7563" data-mp="h04tkgn8rf8r">
                  <div className="flex items-center gap-1.5 text-amber-800 text-xs font-semibold" data-id="element-7564" data-mp="c11ncsviz2z7">
                    <AlertTriangle className="w-3.5 h-3.5" data-id="element-7565" data-mp="70w3vzzesms7" />
                    Groep-mismatch
                  </div>
                  {gradeWarnings.map((w, i) => <p key={i} className="text-xs text-amber-700" data-id="element-7566" data-mp="245ntcnltr6s" data-mpi={i}>
                      {w}
                    </p>)}
                </div>}
            </>}
          <div className="pt-4 flex justify-end gap-2 border-t border-gray-100" data-id="element-7567" data-mp="1we0yu7alxnmv">
            <Button type="button" variant="secondary" onClick={onStopEdit} data-id="element-7568" data-mp="1sfjo7drz9v0p">
              Annuleren
            </Button>
            <Button type="submit" data-id="element-7569" data-mp="3aakd7sutklm3">Opslaan</Button>
          </div>
        </form>
      </div>;
  }
  return <div className="space-y-6" data-id="element-7570" data-mp="3k26kje41e0jk">
      {/* Header */}
      <div className="flex items-center gap-4 pb-4 border-b border-gray-100" data-id="element-7571" data-mp="3k2q6d5wt592v">
        <Avatar name={user.name} size="md" data-id="element-7572" data-mp="3o2qiul0cja7a" />
        <div data-id="element-7573" data-mp="3o5w6dvtbu40w">
          <div className="font-semibold text-gray-900" data-id="element-7574" data-mp="2rdixbczq913d">{user.name}</div>
          <div className="text-sm text-gray-500" data-id="element-7575" data-mp="2rfdhuq736r6x">{user.organization}</div>
          <div className="text-xs text-gray-400 mt-0.5" data-id="element-7576" data-mp="2wdf1bc8fcsix">
            Laatste activiteit: {formatRelativeTime(user.lastActive, true)}
          </div>
        </div>
      </div>

      {/* Quick stats */}
      <div className="grid grid-cols-2 gap-3" data-id="element-7577" data-mp="35e3gia9k0yxe">
        <div className="bg-gray-50 rounded-xl p-4" data-id="element-7578" data-mp="fo04xbcp9plz">
          <div className="text-xs text-gray-500 mb-1" data-id="element-7579" data-mp="fn6v21ig2jzm">Rol</div>
          <Badge variant={ROLE_CONFIG[user.role].variant} size="sm" data-id="element-7580" data-mp="kmtamhlqwcbl">
            {ROLE_CONFIG[user.role].label}
          </Badge>
        </div>
        {user.role === 'learner' && <>
            <div className="bg-emerald-50 rounded-lg p-3" data-id="element-7581" data-mp="3nh2bt0zdu8eh">
              <div className="text-2xl font-bold text-emerald-700" data-id="element-7582" data-mp="3nin6cqlp5rcc">
                {user.avgMastery}%
              </div>
              <div className="text-xs text-emerald-600" data-id="element-7583" data-mp="c4en9dwn0rf">Gem. beheersing</div>
            </div>
            <div className="bg-blue-50 rounded-lg p-3" data-id="element-7584" data-mp="a9vo0m71ipz">
              <div className="text-2xl font-bold text-blue-700" data-id="element-7585" data-mp="ezpemufhgjv4">
                {user.sessionCount}
              </div>
              <div className="text-xs text-blue-600" data-id="element-7586" data-mp="a1nvk9i24kjn">Sessies</div>
            </div>
            <div className="bg-violet-50 rounded-xl p-4" data-id="element-7587" data-mp="o15guew59fo9">
              <div className="text-xs text-gray-500 mb-1" data-id="element-7588" data-mp="jzk8n6uk1pke">XP Totaal</div>
              <div className="text-xl font-bold text-violet-600" data-id="element-7589" data-mp="jy96tcko6rvx">
                {user.xp.toLocaleString('nl-NL')}
              </div>
            </div>
          </>}
        {user.role === 'teacher' && <div className="bg-indigo-50 rounded-lg p-3" data-id="element-7590" data-mp="2bnrziwsxhe14">
            <div className="text-2xl font-bold text-indigo-700" data-id="element-7591" data-mp="2bo1oqi65fh5r">
              {user.linkedCount}
            </div>
            <div className="text-xs text-indigo-600" data-id="element-7592" data-mp="2fr7s9i1vmu8j">Leerlingen</div>
          </div>}
        {user.role === 'parent' && <div className="bg-rose-50 rounded-lg p-3" data-id="element-7593" data-mp="26wjndt07rek9">
            <div className="text-2xl font-bold text-rose-700" data-id="element-7594" data-mp="26uyx4fkxmhs7">
              {user.linkedCount}
            </div>
            <div className="text-xs text-rose-600" data-id="element-7595" data-mp="1s1vo4wlepcd3">Kinderen</div>
          </div>}
      </div>

      {/* Teacher Classrooms with JoinCodes */}
      {user.role === 'teacher' && accounts && (() => {
      const classrooms = getTeacherClassrooms(accounts, user.id);
      if (classrooms.length === 0) return null;
      return <div className="bg-indigo-50 rounded-xl p-4 border border-indigo-100" data-id="element-7596" data-mp="3de6ue8bc8udt">
              <h3 className="text-sm font-semibold text-indigo-900 flex items-center gap-1.5 mb-3" data-id="element-7597" data-mp="3iax9otfbjly6">
                <Users className="w-3.5 h-3.5" data-id="element-7598" data-mp="1zzbth8dhcsqv" /> Klassen & Koppelcodes
              </h3>
              <div className="space-y-2" data-id="element-7599" data-mp="24rd1gi3m7xb3">
                {classrooms.map(c => <div key={c.id} className="bg-white rounded-lg px-3 py-2 border border-indigo-200" data-id="element-7600" data-mp="1r08a03aeibbu">
                    <div className="flex items-center justify-between" data-id="element-7601" data-mp="1uz7btf0u5rz2">
                      <div data-id="element-7602" data-mp="2is5xx8dh92gu">
                        <span className="text-sm font-medium text-gray-900" data-id="element-7603" data-mp="2iortlul96j6r">
                          {c.name}
                        </span>
                        <span className="text-xs text-gray-500 ml-2" data-id="element-7604" data-mp="29qzeorjg1djw">
                          {c.learnerIds.length} leerlingen
                        </span>
                      </div>
                      {c.joinCode && <button onClick={() => {
                navigator.clipboard.writeText(c.joinCode || '');
              }} className="font-mono text-xs font-bold text-indigo-700 bg-indigo-50 px-2 py-1 rounded border border-indigo-200 hover:bg-indigo-100 transition-colors flex items-center gap-1" title="Klik om koppelcode te kopiëren" data-id="element-7605" data-mp="y9g58hf9secw">
                          <Copy className="w-3 h-3" data-id="element-7606" data-mp="zrwxemud0uzc" />
                          {c.joinCode}
                        </button>}
                    </div>
                  </div>)}
              </div>
            </div>;
    })()}

      {/* Learner Classroom Membership */}
      {user.role === 'learner' && accounts && (() => {
      const classroomIds = accounts.relationships.learnerToClassrooms?.[user.id] || [];
      const classrooms = classroomIds.map(id => accounts.classrooms?.[id]).filter(Boolean);
      if (classrooms.length === 0) return null;
      return <div className="bg-violet-50 rounded-xl p-4 border border-violet-100" data-id="element-7607" data-mp="1ofsprnd4xeap">
              <h3 className="text-sm font-semibold text-violet-900 flex items-center gap-1.5 mb-2" data-id="element-7608" data-mp="1tedpzmsdzxz5">
                <Users className="w-3.5 h-3.5" data-id="element-7609" data-mp="1y52klazd5b2i" /> Klas
              </h3>
              <div className="space-y-1.5" data-id="element-7610" data-mp="1y76vwdzldcct">
                {classrooms.map(c => c && <div key={c.id} className="bg-white rounded-lg px-3 py-2 border border-violet-200 flex items-center justify-between" data-id="element-7611" data-mp="15golerwjg8pd">
                        <span className="text-sm font-medium text-gray-900" data-id="element-7612" data-mp="1egtq2uackszu">
                          {c.name}
                        </span>
                        <span className="text-xs text-gray-500" data-id="element-7613" data-mp="1jhhhhh2hnbul">
                          {c.learnerIds.length} klasgenoten
                        </span>
                      </div>)}
              </div>
            </div>;
    })()}

      {/* Link Code for learners */}
      {user.role === 'learner' && user.account && isLearnerAccount(user.account) && <div className="bg-blue-50 rounded-xl p-4 border border-blue-100" data-id="element-7614" data-mp="302jc58hy35n0">
            <div className="flex items-center justify-between mb-2" data-id="element-7615" data-mp="2v7nk48cpki9w">
              <h3 className="text-sm font-semibold text-blue-900 flex items-center gap-1.5" data-id="element-7616" data-mp="2r6twlg36h5hj">
                <LinkIcon className="w-3.5 h-3.5" data-id="element-7617" data-mp="2m9to6yiuvqn9" /> Koppelcode
              </h3>
              <div className="flex gap-1.5" data-id="element-7618" data-mp="2h95zwkm0hnwq">
                {user.account.linkCode && <button onClick={() => {
            navigator.clipboard.writeText(user.account && isLearnerAccount(user.account) ? user.account.linkCode || '' : '');
            setCodeCopied(true);
            setTimeout(() => setCodeCopied(false), 2000);
          }} className="text-xs font-medium text-blue-600 hover:text-blue-800 flex items-center gap-1 px-2 py-1 rounded-lg hover:bg-blue-100 transition-colors" data-id="element-7619" data-mp="2caciwi7inert">
                    <Copy className="w-3 h-3" data-id="element-7620" data-mp="4kxmemyh8p6o" />
                    {codeCopied ? 'Gekopieerd!' : 'Kopieer'}
                  </button>}
                <button onClick={() => {
            const newCode = generateUniqueLinkCode(accounts);
            store.setLearnerLinkCode(user.id, newCode);
            onUserUpdate(prev => {
              if (!prev || !prev.account) return prev;
              return {
                ...prev,
                account: {
                  ...prev.account,
                  linkCode: newCode
                } as Account
              };
            });
          }} className="text-xs font-medium text-blue-600 hover:text-blue-800 flex items-center gap-1 px-2 py-1 rounded-lg hover:bg-blue-100 transition-colors" data-id="element-7621" data-mp="8nu9apv1v7gf">
                  <RefreshCw className="w-3 h-3" data-id="element-7622" data-mp="2ognstgzbdswy" />
                  Nieuwe code
                </button>
              </div>
            </div>
            {user.account.linkCode ? <div className="font-mono font-black text-xl text-blue-700 tracking-widest bg-white rounded-lg px-4 py-2 text-center border border-blue-200" data-id="element-7623" data-mp="2yc7j9nv3zzeb">
                {user.account.linkCode}
              </div> : <p className="text-xs text-blue-600" data-id="element-7624" data-mp="37buoyluvfrd6">
                Geen koppelcode. Klik "Nieuwe code" om er een te genereren.
              </p>}
          </div>}

      {/* Mastery by subject for learners */}
      {user.role === 'learner' && selectedMastery.length > 0 && <div data-id="element-7625" data-mp="3v3t4xj59mawn">
          <h3 className="text-sm font-semibold text-gray-900 mb-3" data-id="element-7626" data-mp="3v16th8f97b95">
            Beheersing per vak
          </h3>
          <div className="space-y-2.5" data-id="element-7627" data-mp="2nragrymx3uqp">
            {selectedMastery.map(s => <div key={s.subjectId} data-id="element-7628" data-mp="2np7henzvkp9b">
                <div className="flex justify-between text-xs mb-1" data-id="element-7629" data-mp="2spc1enzzs24a">
                  <span className="text-gray-600" data-id="element-7630" data-mp="2wrqip3o0ittg">{s.label}</span>
                  <span className="text-gray-500" data-id="element-7631" data-mp="2wqfdczex5e08">
                    {s.count} skills · {s.avg}%
                  </span>
                </div>
                <div className="h-2 bg-gray-100 rounded-full overflow-hidden" data-id="element-7632" data-mp="36hs39lh5pu3m">
                  <div className={`h-2 rounded-full ${getMasteryBand(s.avg).barClass}`} style={{
              width: `${s.avg}%`
            }} data-id="element-7633" data-mp="3bgmn8qqv1opb" />
                </div>
              </div>)}
          </div>
        </div>}

      {/* Actions */}
      <div className="pt-4 border-t border-gray-100 space-y-2" data-id="element-7634" data-mp="1m2ghmg2k63dg">
        <div className="flex gap-2" data-id="element-7635" data-mp="1q3a3z50cra48">
          <Button size="sm" variant="secondary" className="flex-1" onClick={() => onStartEdit(user)} data-id="element-7636" data-mp="1q4bg7ct3u2nj">
            <Edit2 className="w-3.5 h-3.5 mr-1.5" data-id="element-7637" data-mp="1dt0y27mn2vd7" /> Bewerken
          </Button>
          <Button size="sm" variant="danger" className="flex-1" onClick={() => setDeleteConfirmOpen(true)} data-id="element-7638" data-mp="1dqxxwurrvlur">
            <PowerOff className="w-3.5 h-3.5 mr-1.5" data-id="element-7639" data-mp="14tnm98b1lupb" /> Verwijderen
          </Button>
        </div>

        {user.role === 'learner' && onNavigateToLearner && <Button size="sm" variant="secondary" className="w-full" onClick={() => {
        onNavigateToLearner(user.id);
        onClose();
      }} data-id="element-7640" data-mp="1wjs4p1f8s7nw">
            <Eye className="w-3.5 h-3.5 mr-1.5" data-id="element-7641" data-mp="1nhsmi5shqxjd" /> Dashboard
          </Button>}

        {user.role === 'learner' && <div className="pt-2 border-t border-gray-100" data-id="element-7642" data-mp="h4324m35kreg">
            {!resetConfirm ? <button onClick={() => setResetConfirm(true)} className="w-full px-4 py-2.5 text-sm font-semibold text-red-600 bg-red-50 border border-red-200 rounded-xl hover:bg-red-100 transition-colors" data-id="element-7643" data-mp="c23zxfj5g0j3">
                <RotateCcw className="w-3.5 h-3.5 inline mr-1.5" data-id="element-7644" data-mp="2280jce5u65bi" />
                Voortgang resetten
              </button> : <div className="p-3 bg-red-50 border border-red-200 rounded-xl space-y-2" data-id="element-7645" data-mp="277ddzl2y8gon">
                <p className="text-xs font-semibold text-red-800" data-id="element-7646" data-mp="2uxqxgn7gx77m">
                  ⚠️ Weet je het zeker? Dit kan niet ongedaan worden gemaakt.
                </p>
                <div className="flex gap-2" data-id="element-7647" data-mp="2l6wcrn9vefoa">
                  <button onClick={() => setResetConfirm(false)} className="flex-1 py-2 text-xs font-semibold text-gray-600 bg-white border border-gray-200 rounded-lg hover:bg-gray-50" data-id="element-7648" data-mp="2l639iwa4e8aw">
                    Annuleren
                  </button>
                  <button onClick={() => {
              store.resetLearnerProgress(user.id);
              setResetConfirm(false);
            }} className="flex-1 py-2 text-xs font-semibold text-white bg-red-600 rounded-lg hover:bg-red-700" data-id="element-7649" data-mp="3inlbh83ix1tg">
                    Definitief resetten
                  </button>
                </div>
              </div>}
          </div>}
      </div>

      <ConfirmDialog open={deleteConfirmOpen} title="Account verwijderen" message={`Weet je zeker dat je ${user.name} wilt verwijderen? Dit kan niet ongedaan worden gemaakt.`} confirmLabel="Verwijderen" cancelLabel="Annuleren" variant="danger" onConfirm={() => {
      store.deleteAccount(user.id);
      setDeleteConfirmOpen(false);
      onClose();
    }} onCancel={() => setDeleteConfirmOpen(false)} data-id="element-7650" data-mp="o8yonm5awmy1" />
    </div>;
}